import React, { useCallback } from 'react'
import Grid from '@material-ui/core/Grid'
import Divider from '@material-ui/core/Divider'
import Container from '@material-ui/core/Container'
import Typography from '@material-ui/core/Typography'
import { ThemeProvider } from '@material-ui/core'
import { useParams } from 'react-router-dom'
import { DownloadMyPGAsection } from 'components/download-mypga-section'
import TransactionSummary from 'pages/schedule/ReviewBooking/transaction-summary'
import { useStyles } from '../styles'
import { gtmEvent } from 'lib/gtm'
import { useAuth } from 'lib/auth'
import { useIsMobile } from 'lib/hooks'
import CoachLessonHeader from '../../schedule/components/CoachLessonHeader'
import InitialsAvatar from '../../../components/avatars/initials-avatar'
import SummaryBookingButtons from '../../schedule/components/SummaryBookingButtons'
import themes, { extendTheme } from 'themes'

const PrivateLessonPurchaseConfirmation = ({ lesson }) => {
  const classes = useStyles()
  const { isCoach, user } = useAuth()
  const isMobile = useIsMobile()
  const { idOrSlug, lessonTypeId } = useParams()

  const { lesson: product, transactions, student } = lesson || {}

  const { coach, coachFacility, title, startDateTime, endDateTime } =
    product || {}

  const { registrationPayment, amountInCents } = transactions?.[0] || {}
  const { cardBrand, last4 } = registrationPayment || {}

  const coachBooksOnBehalfOfStudent =
    user?.coach?.coachProfile?.slug === idOrSlug ||
    user?.externalId === idOrSlug

  const durationInMinutes =
    startDateTime && endDateTime
      ? Math.round((new Date(endDateTime) - new Date(startDateTime)) / 60000)
      : 0

  const lessonType = {
    title,
    durationInMinutes,
    priceInCents: amountInCents,
    facilities: [coachFacility],
    startDateTime,
  }

  const paymentRequired = !isCoach && amountInCents > 0

  const studentName = `${student?.firstName} ${student?.lastName}`

  const coachBookingUrl = coach?.bookingUrl || ''

  const bookNewSessionUrl = `/coach/${idOrSlug}/schedule?lessonTypeId=${lessonTypeId}&locationId=${coachFacility?.id}`

  const onBookAnotherSession = useCallback(() => {
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'clicked-book-another-session-button',
    })
    window.open(bookNewSessionUrl, '_blank')
  }, [bookNewSessionUrl])

  const onViewCoachOfferings = useCallback(() => {
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'clicked-view-coach-offerings-button',
    })
    if (coachBookingUrl) {
      window.location.href = coachBookingUrl
    }
  }, [coachBookingUrl])

  return (
    <ThemeProvider theme={extendTheme(themes.offeringsRefresh)}>
      <Container className={classes.summaryWrapper} maxWidth="md">
        {paymentRequired && (
          <TransactionSummary
            title={title}
            priceInCents={amountInCents}
            cardBrand={cardBrand}
            lastDigits={last4}
          />
        )}
        <Grid item xs={12} style={{ marginTop: '24px' }}>
          <CoachLessonHeader lessonType={lessonType} coach={coach} />
        </Grid>
        <Divider className={classes.divider} />
        <Grid
          container
          item
          xs={12}
          sm={6}
          spacing={2}
          className={classes.rowWrapped}
        >
          <InitialsAvatar name={`${studentName}`} classes={classes} />
          <div className={classes.infoWrapped}>
            <Typography
              variant="caption"
              className={`${classes.fontBolded} ${classes.captionText}`}
            >
              {studentName}
            </Typography>
            <Typography
              variant="caption"
              className={`${classes.captionText} ${classes.lighterText}`}
            >
              Student
            </Typography>
          </div>
        </Grid>
        <Grid
          container
          spacing={1}
          direction={isMobile ? 'column' : 'row'}
          justifyContent={isMobile ? 'center' : 'space-around'}
          style={{ marginTop: '1.5rem' }}
        >
          <SummaryBookingButtons
            isMobile={isMobile}
            coachIsViewingOwnSchedule={coachBooksOnBehalfOfStudent}
            onBookAnotherSession={onBookAnotherSession}
            onViewCoachOfferings={onViewCoachOfferings}
            classes={classes}
          />
        </Grid>
        <DownloadMyPGAsection />
      </Container>
    </ThemeProvider>
  )
}

export default PrivateLessonPurchaseConfirmation
